import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'preact/hooks';
import { X } from 'lucide-preact';
import { useToast } from '@/hooks/useToast';
import { useLookups } from '@/hooks/useLookups';
import { contactsApi } from '@/api/contacts';
import { isValidEmail } from '@/utils/validation';
import { useAIForm } from '@/hooks/useAIForm';
import { TextBox, TextBoxField } from '@/components';
import { Select } from '@/components/common/Select';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
export function OnlineEventParticipantEditModal({ open, onClose, speaker, onSave, workspaceId, webId, campaignId }) {
    const { showError } = useToast();
    const { getSpeakerTypes } = useLookups();
    const speakerTypeOptions = getSpeakerTypes() || [];
    // UI-only state
    const [autoUpdate, setAutoUpdate] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [saving, setSaving] = useState(false);
    // Form + AI integration
    const { form } = useAIForm({
        mode: 'onChange',
        defaultValues: {
            speakerType: speaker?.SpeakerType || '',
            companyId: speaker?.CompanyId || '',
            companyName: speaker?.CompanyName || '',
            email: speaker?.Email || '',
            title: speaker?.Title || '',
            bio: speaker?.Bio || '',
        }
    }, {
        aiContext: {
            workspaceId: workspaceId || '',
            campaignId,
            webId,
            location: ChatLocations.WebSpeakerEditor,
            entityType: ChatEntityTypes.Web,
            entityId: webId,
            viewMode: ChatViewModes.Edit,
        },
        fields: open && speaker ? [
            { aiName: 'speakerBio', formPath: 'bio', displayName: 'Speaker Bio', fieldType: 'textarea', maxLength: 1000 },
            { aiName: 'speakerTitle', formPath: 'title', displayName: 'Speaker Title', fieldType: 'text', maxLength: 200 },
        ] : [],
        loadedData: open && speaker ? { speakerName: speaker.Name, speakerCompany: speaker.CompanyName } : undefined,
        contextSuppressed: !open,
    });
    const { register, control, watch, reset, setValue } = form;
    // Watch form values for validation
    const emailValue = watch('email');
    const titleValue = watch('title');
    const bioValue = watch('bio');
    const speakerTypeValue = watch('speakerType');
    const companyNameValue = watch('companyName');
    const companyIdValue = watch('companyId');
    // Reset when opened
    useEffect(() => {
        if (!open || !speaker)
            return;
        reset({
            speakerType: speaker.SpeakerType || '',
            companyId: speaker.CompanyId || '',
            companyName: speaker.CompanyName || '',
            email: speaker.Email || '',
            title: speaker.Title || '',
            bio: speaker.Bio || '',
        });
        setAutoUpdate(false);
        setCompanies([]);
        setPositions([]);
        if (speaker.ContactId) {
            void enrichFromContactId(speaker.ContactId);
        }
    }, [open, speaker?.ContactId]);
    const enrichFromContactId = async (contactId) => {
        setLoadingCompanies(true);
        try {
            const res = await contactsApi.lookupByContactId(contactId, { includeCompanies: true, includeSchools: false });
            if (res.data) {
                setCompanies(res.data.Companies || []);
                setPositions(res.data.Positions || []);
                const currentCompanyId = form.getValues('companyId');
                if (contactId && currentCompanyId) {
                    const comp = (res.data.Companies || []).find(c => c.Id === currentCompanyId);
                    if (comp)
                        setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '');
                }
            }
            else if (res.error) {
                showError('Lookup failed', res.error);
            }
        }
        catch (e) {
            showError('Lookup error', e.message);
        }
        finally {
            setLoadingCompanies(false);
        }
    };
    const handleSelectCompany = (cid) => {
        setValue('companyId', cid);
        if (!cid)
            return;
        const comp = companies.find(c => c.Id === cid);
        if (comp) {
            setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '');
        }
        if (autoUpdate) {
            const related = positions.filter(p => p.CompanyId === cid);
            if (related.length) {
                const latest = related.sort((a, b) => {
                    const ad = a.StartDate ? Date.parse(a.StartDate) : 0;
                    const bd = b.StartDate ? Date.parse(b.StartDate) : 0;
                    return bd - ad;
                })[0];
                if (latest?.Title)
                    setValue('title', latest.Title.slice(0, 200));
                if (latest?.Description)
                    setValue('bio', latest.Description.slice(0, 1000));
            }
        }
    };
    // Validation
    const emailValid = isValidEmail(emailValue);
    const emailRequiredError = attemptedSave && !emailValue.trim();
    const emailInvalidError = !!emailValue.trim() && !emailValid;
    const emailLengthExceeded = emailValue.trim().length > 254;
    const titleRequiredError = attemptedSave && !titleValue.trim();
    const titleLengthExceeded = titleValue.trim().length > 200;
    const speakerTypeRequiredError = attemptedSave && !speakerTypeValue.trim();
    const bioLengthExceeded = bioValue.trim().length > 1000;
    const canSave = speaker
        && !emailRequiredError
        && !emailInvalidError
        && !emailLengthExceeded
        && !titleRequiredError
        && !titleLengthExceeded
        && !speakerTypeRequiredError
        && !bioLengthExceeded
        && emailValue.trim()
        && titleValue.trim()
        && companyNameValue.trim()
        && speakerTypeValue.trim();
    const handleSave = async () => {
        setAttemptedSave(true);
        if (!speaker || !canSave)
            return;
        setSaving(true);
        try {
            const formValues = form.getValues();
            let finalCompanyName = formValues.companyName.trim();
            if (formValues.companyId && !finalCompanyName) {
                const comp = companies.find(c => c.Id === formValues.companyId);
                if (comp)
                    finalCompanyName = (comp.Name || comp.UniversalName || comp.NormalizedName || '').trim();
            }
            await onSave({
                ContactId: speaker.ContactId,
                SpeakerType: formValues.speakerType.trim(),
                CompanyId: formValues.companyId || undefined,
                CompanyName: finalCompanyName,
                Email: formValues.email.trim(),
                Title: formValues.title.trim(),
                Bio: formValues.bio.trim() || undefined,
            });
            onClose();
        }
        catch (e) {
            showError('Update failed', e.message || 'Unable to update participant');
        }
        finally {
            setSaving(false);
        }
    };
    // Speaker type select options
    const speakerTypeSelectOptions = speakerTypeOptions.map(o => ({ value: o.Key, label: o.Description }));
    if (!open || !speaker)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: [_jsx("div", { className: "absolute inset-0 bg-slate-900/60 backdrop-blur-sm", onClick: () => !saving && onClose() }), _jsxs("div", { className: "relative w-full max-w-3xl rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-5 flex flex-col gap-5", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "Edit Participant" }), _jsx("button", { type: "button", onClick: () => !saving && onClose(), className: "p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300", "aria-label": "Close", children: _jsx(X, { className: "w-4 h-4" }) })] }), _jsxs("div", { className: "grid gap-4 md:grid-cols-2", children: [_jsx(TextBox, { labelSize: "xs", label: "Name", name: "name", value: speaker.Name, onChange: () => { }, disabled: true, containerClassName: "md:col-span-2" }), _jsx(Select, { labelSize: "xs", label: "Speaker Type", options: speakerTypeSelectOptions, placeholder: "Select type", error: speakerTypeRequiredError ? 'Invalid SpeakerType.' : undefined, ...register('speakerType') }), _jsxs("div", { className: "md:col-span-2", children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsx("label", { className: "block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400", children: "Company" }), companies.length > 0 && (_jsxs("label", { className: "inline-flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300 font-medium", children: [_jsx("input", { id: "auto-update", type: "checkbox", checked: autoUpdate, onChange: (e) => setAutoUpdate(e.currentTarget.checked), className: "w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500" }), _jsx("span", { children: "Auto-update Title & Bio" })] }))] }), loadingCompanies ? (_jsxs("div", { className: "flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400", children: [_jsx("div", { className: "w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), " Loading companies\u2026"] })) : companies.length > 0 ? (_jsx(Select, { name: "companyId", options: companies.map(c => ({ value: c.Id, label: c.Name || c.UniversalName || c.NormalizedName || 'Company' })), placeholder: "Select company", value: companyIdValue, onChange: (e) => handleSelectCompany(e.currentTarget.value) })) : (_jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400", children: "No companies found from lookup." })), companyIdValue && companyNameValue && (_jsx("div", { className: "mt-1 text-[11px] text-slate-500 dark:text-slate-400 truncate", children: companyNameValue }))] }), _jsx(TextBoxField, { control: control, name: "companyName", labelSize: "xs", label: "Company Name", maxLength: 200, placeholder: "Company name", error: attemptedSave && !companyNameValue.trim() ? 'Company Name is required.' : companyNameValue.trim().length > 200 ? 'Company Name exceeds maximum length of 200 characters.' : undefined }), _jsx(TextBoxField, { control: control, name: "title", labelSize: "xs", label: "Title", maxLength: 200, error: titleRequiredError ? 'Speaker Title is required.' : titleLengthExceeded ? 'Speaker Title exceeds maximum length of 200 characters.' : undefined, placeholder: "Title" }), _jsx(TextBoxField, { control: control, name: "email", labelSize: "xs", label: "Email", type: "email", maxLength: 320, error: emailRequiredError ? 'Speaker Email is required.' : emailInvalidError ? 'Speaker Email is invalid.' : emailLengthExceeded ? 'Speaker Email exceeds maximum length of 320 characters.' : undefined, placeholder: "name@example.com" }), _jsx("div", { className: "md:col-span-2", children: _jsx(TextBoxField, { control: control, name: "bio", labelSize: "xs", label: "Bio (optional)", multiline: true, rows: 5, showCharCounter: true, maxLength: 1000, error: bioLengthExceeded ? 'Speaker Bio exceeds maximum length of 1000 characters.' : undefined, placeholder: "Short bio" }) })] }), _jsxs("div", { className: "flex items-center justify-between pt-2", children: [_jsx("button", { type: "button", onClick: () => !saving && onClose(), className: "px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700", children: "Cancel" }), _jsxs("button", { type: "button", disabled: !canSave || saving, onClick: handleSave, className: "px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2", children: [saving && _jsx("div", { className: "w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" }), saving ? 'Saving...' : 'Save'] })] })] })] }));
}
